
import CompletedMissionMap from '@/components/Mission/CompletedMissionMap.vue';
import { ImplementKeys, Parameters } from '@/services/Parameters';
import FieldStore from '@/store/modules/fields';
import GlobalStore from '@/store/modules/global';
import MissionStore from '@/store/modules/missions';
import PathStore from '@/store/modules/paths';
import { Convert as UserConvert, FlatUser, SingleUser } from '@/flightpath-components/models/CognitoUser';
import { DeployedSinglePathMission } from '@/flightpath-components/models/DeployedSinglePathMission';
import { FieldOperationsModel } from '@/flightpath-components/models/FieldOperationsModel';
import { SimplePath } from '@/flightpath-components/models/SimplePath';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import { AxiosResponse } from 'axios';
import html2canvas from 'html2canvas';
import { Container } from 'inversify';
import jsPdf from 'jspdf';
import { Component, Inject, Vue } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import { getObjectBlob, getObjectHead } from '@/flightpath-components/utils/S3Helper';
import { Auth } from '@aws-amplify/auth';
import { downloadFile } from '@/flightpath-components/utils/FileHelper';
import { S3Client } from "@aws-sdk/client-s3";
import awsExports from '@/aws-exports';
import { Mixins } from 'vue-property-decorator';
import NotificationMixIn from '@/mixins/notifications';

const shapefileSizeLimit = 500000000; //500 MB //TODO: FLIG-2745 - Remove it

@Component({
  name: 'MissionReport',
  components: {
    CompletedMissionMap
  }
})
export default class MissionReport extends  Mixins(NotificationMixIn) {
  public fieldStore = getModule(FieldStore, this.$store);
  public missionStore = getModule(MissionStore, this.$store);
  public pathStore = getModule(PathStore, this.$store);
  public globalStore = getModule(GlobalStore, this.$store);
  public shapefileBucket : string = process.env.VUE_APP_SHAPEFILES_S3_BUCKET || "exxact-shapefiles";

  errorMessage = '';
  loading = false;
  operatorEmail = '';
  currentMission: DeployedSinglePathMission | null = null;
  path: SimplePath | null = null;
  field: FieldOperationsModel | null = null;
  millisecondsToHoursConversion = 36e5;

  // @Inject()
  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  // IoC container provided from App.ts is injected here
  @Inject('container')
  private container!: Container;

  closeMissionReport(): void {
    this.$router.push({ name: 'CompletedMissions' });
  }
  users: Array<FlatUser> = [];

  async assignOperatorEmail(): Promise<void> {
    if (this.flightPathService != undefined) await this.flightPathService.updateToken();

    this.loading = true;
    this.operatorEmail = '';

    try {
      const userRequest = await this.getUser(this.currentMission?.DeployerUserID ?? '');
      this.operatorEmail = userRequest != null && userRequest?.User != null ? this.userLocalAttribute(userRequest.User, 'email') : '';
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  async getUser(id: string) {
    const response = await this.flightPathService?.getUser(id);
    return UserConvert.toSingleUsersWithGroup(JSON.stringify(response?.data));
  }

  userLocalAttribute(user: SingleUser, attribute: string): string {
    let returnValue = '';
    user.UserAttributes?.forEach((value) => {
      if (value.Name === attribute) {
        returnValue = value.Value;
      }
    });
    return returnValue;
  }

  created(): void {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);
  }

  generateCsvUri(): string {
    // create csv
    const rows = [
      [this.$t('views.missions.name'), this.currentMission?.MissionName],
      [this.$t('views.missions.field'), this.fieldName],
      [this.$t('views.missions.route'), this.routeName],
      [this.$t('views.missions.dialog.total_area'), this.totalArea, 'ha'],
      [this.$t('views.missions.machine'), this.currentMission?.TargetMachine.MachineName],
      [this.$t('views.missions.dialog.mission_type'), this.missionType],
      [this.$t('views.missions.tool_type'), this.toolType],
      [this.$t('views.missions.duration'), this.humanReadableDuration],
      [this.$t('views.missions.start_time'), this.startedOn],
      [this.$t('views.missions.finished_on'), this.finishedOn],
      [this.$t('views.missions.end_time'), this.endedOn],
      [this.$t('views.missions.operator'), this.operatorEmail]
    ];

    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF;' + rows.map((x) => x.join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);

    return encodedUri;
  }

  createAndDownloadCsv(): void {
    const csvUri = this.generateCsvUri();

    // download file
    const fileName = `${this.currentMission?.MissionName.replaceAll(' ', '_')}_${this.currentMission?.MissionDeploymentID}.csv`;
    const link = document.createElement('a');
    link.setAttribute('href', csvUri);
    link.setAttribute('download', fileName);
    document.body.appendChild(link); // Required for FF

    link.click();
  }

  createAndDownloadPdf(): void {
    const rows = [
      { name: this.$t('views.missions.name').toString(), value: this.currentMission?.MissionName ?? '' },
      { name: this.$t('views.missions.field').toString(), value: this.fieldName ?? '' },
      { name: this.$t('views.missions.route').toString(), value: this.routeName ?? '' },
      { name: this.$t('views.missions.dialog.total_area').toString(), value: (this.totalArea ?? '') + 'ha' },
      { name: this.$t('views.missions.machine').toString(), value: this.currentMission?.TargetMachine.MachineName ?? '' },
      { name: this.$t('views.missions.dialog.mission_type').toString(), value: this.missionType ?? '' },
      { name: this.$t('views.missions.tool_type').toString(), value: this.toolType ?? '' },
      { name: this.$t('views.missions.duration').toString(), value: this.humanReadableDuration ?? '' },
      { name: this.$t('views.missions.start_time').toString(), value: this.startedOn ?? '' },
      { name: this.$t('views.missions.finished_on').toString(), value: this.finishedOn ?? '' },
      { name: this.$t('views.missions.end_time').toString(), value: this.endedOn ?? '' },
      { name: this.$t('views.missions.operator').toString(), value: this.operatorEmail ?? '' }
    ];
    const map = this.$refs.map as HTMLElement;
    html2canvas(map, {
      windowWidth: 1080,
      windowHeight: 720,
      useCORS: true
    }).then((canvas) => {
      const doc = new jsPdf();
      doc.addImage(canvas as HTMLCanvasElement, 'base64', 10, 10, 190, 130);
      doc.table(10, 150, rows, ['name', 'value'], { printHeaders: false });
      doc.save('report.pdf');
    });
  }

  async exportShapefile() {

    try {
      if (!this.currentMission)
      {
        this.showNotification(this.$t('views.missions.invalid_mission').toString());
        return false;
      }
      
      this.loading = true;
      const cognitoUserCreds = await Auth.currentUserCredentials();
      const s3Client = new S3Client({
        region: awsExports.aws_project_region,
        credentials:{
            accessKeyId: cognitoUserCreds.accessKeyId,
            secretAccessKey: cognitoUserCreds.secretAccessKey,
            sessionToken: cognitoUserCreds.sessionToken
        }
      });

      const file = await getObjectBlob(s3Client, this.shapefileBucket, this.currentMission.ShapefileZipS3Key);

      if ((file.size ?? 0) > shapefileSizeLimit)
      {
        this.showNotification(this.$t('views.missions.shapefiles_too_big').toString());
        this.loading = false;
        return false;
      }

      const missionName = this.currentMission?.MissionName ?? this.currentMission!.MissionID;
      downloadFile(this.currentMission?.DeployedTimeUTC ?? new Date(), `${missionName}-${this.currentMission.TargetMachine.MachineName}`, file);

    } catch (error) {
      console.log(error);
      this.showNotification(this.$t('views.missions.shapefiles_error_message').toString());
    } finally {
      this.loading = false;
    }
  }

  async mounted(): Promise<void> {
    this.currentMission = await this.getCurrentMission();
    if (this.currentMission != undefined) {
      this.field = await this.getField(this.currentMission);
      this.path = await this.getPath(this.currentMission);
    }

    await this.assignOperatorEmail();
  }

  async getField(mission: DeployedSinglePathMission): Promise<FieldOperationsModel | null> {
    if (!this.flightPathService) return null;

    try {
      const field = await this.flightPathService.getFieldDefinition(mission.ReferenceFieldID, this.globalStore.accountId);
      return FieldOperationsModel.toModel(JSON.stringify(field.data));
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  async getPath(mission: DeployedSinglePathMission): Promise<SimplePath | null> {
    if (!this.flightPathService) return null;
    try {
      const path = await this.flightPathService.getPathByPathId(mission.ReferencePathID, this.globalStore.accountId);
      return SimplePath.toModel(JSON.stringify(path.data));
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  async getCurrentMission(): Promise<DeployedSinglePathMission | null> {
    this.errorMessage = '';
    const deploymentId = this.$route.params?.deploymentId;
    if (deploymentId != undefined) {
      try {
        const response = await this.flightPathService?.getDeployedSinglePathMission(deploymentId, this.globalStore.accountId);
        return DeployedSinglePathMission.toModel(JSON.stringify(response?.data));
      } catch (err) {
        const response = err.response as AxiosResponse<any>;
        const message = response.data;
        const displayMessage = Object.prototype.hasOwnProperty.call(message, 'Error') ? message.Error : response.statusText;
        console.error(response.data);
        this.errorMessage = `Error: ${displayMessage}`;
        return null;
      }
    }
    return null;
  }

  get machineFilterApplied() {
    return this.$route.params?.machineId?.length > 0;
  }

  get fieldName(): string {
    return this.field?.FieldName ?? 'None';
  }

  get routeName(): string {
    return this.path?.Name ?? 'None';
  }

  get totalArea(): string {
    return this.missionStore.workAreaHectares.toString();
  }

  get missionType(): string {
    if (this.currentMission == null || this.currentMission?.ApplicationParameters === '') return '';
    if (this.currentMission.getApplicationParameters().get(Parameters.implementType) == ImplementKeys.sprayer) {
      return this.$t('views.missions.spraying_mission').toString();
    } else {
      return this.$t('views.missions.tillage').toString();
    }
  }

  get toolType(): string {
    if (this.currentMission == null || this.currentMission?.ApplicationParameters === '') return '';
    return this.currentMission.getApplicationParameters().get(Parameters.implementType) ?? '';
  }

  get implementType() {
    if (this.currentMission == null || this.currentMission?.ApplicationParameters === '') return '';
    const implement = this.currentMission.getApplicationParameters().get(Parameters.implementType) ?? '';
    const key = implement != '' ? 'views.missions.' + implement : '';
    return this.$te(key) ? this.$t(key) : '';
  }

  get duration(): number {
    if (this.currentMission != null) {
      const hours =
        (new Date(this.currentMission.UpdateTimeUTC).getTime() - new Date(this.currentMission.DeployedTimeUTC).getTime()) / this.millisecondsToHoursConversion;
      return Math.round((hours + Number.EPSILON) * 100) / 100;
    }
    return 0;
  }

  get humanReadableDuration(): string {
    if (this.currentMission != null) {
      const msDuration = new Date(this.currentMission.UpdateTimeUTC).getTime() - new Date(this.currentMission.DeployedTimeUTC).getTime();
      const h = Math.floor(msDuration / 1000 / 60 / 60);
      const m = Math.floor((msDuration / 1000 / 60 / 60 - h) * 60);

      // To get time format 00:00:00
      // const seconds: string = s < 10 ? `0${s}` : `${s}`;
      // const minutes: string = m < 10 ? `0${m}` : `${m}`;
      // const hours: string = h < 10 ? `0${h}` : `${h}`;

      return `${h} hrs ${m} min`;
    }
    return '0 hrs';
  }

  get operator(): string {
    return this.operatorEmail;
  }

  get startedOn(): string {
    if (this.currentMission != null) {
      const date = new Date(this.currentMission.DeployedTimeUTC);
      return `${date.toLocaleTimeString()}`;
    }
    return ``;
  }

  get finishedOn(): string {
    if (this.currentMission != null) {
      const date = new Date(this.currentMission.UpdateTimeUTC);
      return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
    }
    return ``;
  }

  get endedOn(): string {
    if (this.currentMission != null) {
      const date = new Date(this.currentMission.UpdateTimeUTC);
      return `${date.toLocaleTimeString()}`;
    }
    return ``;
  }
}
